/**
 * Cloudflare Turnstile widget (§145) rendered explicitly with the site key from
 * GET /api/v1/public/config. Tokens are single-use: callers reset the widget after every
 * request that consumed one. The server always validates the token (Siteverify).
 */
import { type Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useRuntimeConfig } from '../config/runtime-config';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** Height of the „flexible“ widget – reserved up front so nothing shifts when it appears. */
const WIDGET_HEIGHT_PX = 65;

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  language: string;
  size: 'flexible';
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded without API'));
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.append(script);
  });
  return scriptPromise;
}

export interface TurnstileHandle {
  reset: () => void;
}

export interface TurnstileProps {
  /** New token, or null when it expired / was reset. */
  onToken: (token: string | null) => void;
  /** Analytics label in the Turnstile dashboard. */
  action: string;
  ref?: Ref<TurnstileHandle>;
}

export function Turnstile({ onToken, action, ref }: TurnstileProps) {
  const { turnstileSiteKey } = useRuntimeConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const apiRef = useRef<TurnstileApi | null>(null);
  const onTokenRef = useRef(onToken);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current(null);
      if (apiRef.current && widgetIdRef.current !== null) apiRef.current.reset(widgetIdRef.current);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then(
      (api) => {
        const container = containerRef.current;
        if (cancelled || !container) return;
        apiRef.current = api;
        widgetIdRef.current = api.render(container, {
          sitekey: turnstileSiteKey,
          action,
          language: 'de',
          size: 'flexible',
          callback: (token) => {
            onTokenRef.current(token);
          },
          'expired-callback': () => {
            onTokenRef.current(null);
          },
          'error-callback': () => {
            onTokenRef.current(null);
          },
        });
      },
      (error: unknown) => {
        console.warn('turnstile.load_failed', error instanceof Error ? error.message : error);
        if (!cancelled) setLoadFailed(true);
      },
    );
    return () => {
      cancelled = true;
      if (apiRef.current && widgetIdRef.current !== null)
        apiRef.current.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [turnstileSiteKey, action]);

  return (
    <Box sx={{ minHeight: WIDGET_HEIGHT_PX }}>
      <div ref={containerRef} />
      {loadFailed ? (
        <Typography role="alert" color="error" variant="body2">
          Die Sicherheitsprüfung konnte nicht geladen werden. Bitte prüfen Sie Ihre
          Internetverbindung und laden Sie die Seite neu.
        </Typography>
      ) : null}
    </Box>
  );
}
